import React, { FC, ReactNode, useState } from 'react';
import styled from 'styled-components';
import { TypographyProps } from '@clds/typography';
import { ButtonMenu } from '@cld/button';
import CopyToClipboard, { CopyToClipboardProps } from '@cld/copy-to-clipboard';
import { Icon } from '@cld/icon';
import CollapseContext from './collapsible/CollapseContext';
import AssetUrlContainer from './container/AssetUrlContainer';
import ScrollableLinesWrapContainer from './container/ScrollableLinesWrapContainer';
import { theme as t } from './AssetUrlTheme';

const defaultCollapsedIcon = <Icon name="cloud" />;
const defaultExpandedIcon = <Icon name="cloud" />;

const ButtonWithMargin = styled(ButtonMenu)`
  margin-right: ${t.buttonMargin};
  ${Icon} path {
    fill: ${t.iconColor};
  }
`;

const ScrollableLinesWrapContainerWithPointer = styled(ScrollableLinesWrapContainer)`
  cursor: pointer;
`;

export interface CopyableAssetUrlProps {
  children: ReactNode;
  isCollapsedInitially?: boolean;
  showCollapseButton?: boolean;
  collapsedIcon?: ReactNode;
  expandedIcon?: ReactNode;
  copyToClipboardProps: Omit<CopyToClipboardProps, 'children'>;
  size?: TypographyProps['size'];
  noWrap?: boolean;
}

const CopyableAssetUrl: FC<React.PropsWithChildren<CopyableAssetUrlProps>> = ({
  children,
  isCollapsedInitially = true,
  showCollapseButton = true,
  collapsedIcon = defaultCollapsedIcon,
  expandedIcon = defaultExpandedIcon,
  copyToClipboardProps,
  size = 'sm',
  noWrap = false,
}) => {
  const [collapsed, setCollapsed] = useState(isCollapsedInitially);
  const toggleCollapsed = () => {
    setCollapsed((collapsed) => !collapsed);
  };
  return (
    <AssetUrlContainer>
      <CollapseContext.Provider value={collapsed}>
        {showCollapseButton && (
          <ButtonWithMargin size="sm" shape="pill" onClick={toggleCollapsed}>
            {collapsed ? collapsedIcon : expandedIcon}
          </ButtonWithMargin>
        )}
        <CopyToClipboard {...copyToClipboardProps}>
          <ScrollableLinesWrapContainerWithPointer size={size} noWrap={noWrap}>
            {children}
          </ScrollableLinesWrapContainerWithPointer>
        </CopyToClipboard>
      </CollapseContext.Provider>
    </AssetUrlContainer>
  );
};

export default CopyableAssetUrl;
